/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { App } from 'antd'
import { FormikProvider, useFormik } from 'formik'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  API_ACCESS_SUCCESS_MESSAGES,
  PLUGIN_KEY_ENVIRONMENT_OPTIONS,
  PLUGIN_KEY_FIELD_MAP,
  PLUGIN_KEY_FIELD_TITLES,
  PLUGIN_KEY_INITIAL_VALUES,
  applyApiAccessError,
  formatWebsites,
  toPluginKeyPayload,
  type PluginKey,
  type PluginKeyFormValues,
} from '@sportninja/common/features/account/apiAccess'
import BaseModal from 'src/components/ts-components/BaseModal'
import { formatSuccessToast } from 'src/helpers/formatSuccessToast'
import {
  SelectField,
  SwitchField,
  TextAreaField,
  TextField,
} from '../../components/FormikField'
import { usePluginKeyMutations } from '../hooks/usePluginKeyMutations'

interface PluginKeyModalProps {
  isOpen: boolean
  onClose: () => void
  accountUid: string
  /** The key being edited; absent to create one. */
  pluginKey?: PluginKey | null
}

const toFormValues = (pluginKey: PluginKey): PluginKeyFormValues => ({
  name: pluginKey.name,
  environment: pluginKey.environment,
  websites: formatWebsites(pluginKey.allowed_origins),
  allowSandboxed: pluginKey.allow_sandboxed,
})

/**
 * Create or edit a Website Plugin key: its name, the websites allowed to
 * embed the Website Plugin, and whether sandboxed site builders may. The environment is part of
 * the key, so it is chosen once and read-only afterwards.
 */
const PluginKeyModal = (props: PluginKeyModalProps) =>
  // Mounted only while open, so each open starts from fresh values.
  props.isOpen ? <PluginKeyForm {...props} /> : null

const PluginKeyForm = ({
  isOpen,
  onClose,
  accountUid,
  pluginKey,
}: PluginKeyModalProps) => {
  const { message } = App.useApp()
  const { create, update } = usePluginKeyMutations(accountUid)
  const mode = pluginKey ? 'edit' : 'create'

  const formik = useFormik<PluginKeyFormValues>({
    initialValues: pluginKey
      ? toFormValues(pluginKey)
      : PLUGIN_KEY_INITIAL_VALUES,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { setErrors, setTouched }) => {
      const payload = toPluginKeyPayload(values, mode)
      try {
        if (pluginKey) {
          await update.mutateAsync({ pluginKeyId: pluginKey.id, payload })
          message.success(
            formatSuccessToast(API_ACCESS_SUCCESS_MESSAGES.pluginKeyUpdated)
          )
        } else {
          await create.mutateAsync(payload)
          message.success(
            formatSuccessToast(API_ACCESS_SUCCESS_MESSAGES.pluginKeyCreated)
          )
        }
        onClose()
      } catch (err) {
        applyApiAccessError(err, {
          fieldMap: PLUGIN_KEY_FIELD_MAP,
          titleMap: PLUGIN_KEY_FIELD_TITLES,
          setErrors,
          setTouched: (touched) => setTouched(touched, false),
          notifyError: message.error,
        })
      }
    },
  })

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={capitalizeWords(
        pluginKey ? 'Edit Website Plugin Key' : 'Create Website Plugin Key'
      )}
      onOk={formik.submitForm}
      okText={pluginKey ? 'Save' : 'Create'}
      isLoading={formik.isSubmitting}
    >
      <FormikProvider value={formik}>
        <div css={formStyle}>
          <div css={twoColumnStyle}>
            <TextField
              name='name'
              label={capitalizeWords('Name')}
              placeholder={capitalizeWords('e.g. league website')}
            />
            <SelectField
              name='environment'
              label={capitalizeWords('Environment')}
              placeholder={capitalizeWords('select environment')}
              options={PLUGIN_KEY_ENVIRONMENT_OPTIONS}
              disabled={mode === 'edit'}
              hint={
                formik.values.environment === 'test'
                  ? 'For testing: also works on localhost, and payments are limited.'
                  : undefined
              }
              hintColor={colors.PRIMARY_300}
            />
          </div>
          <TextAreaField
            name='websites'
            label={capitalizeWords('Hosted Websites')}
            placeholder={capitalizeWords('one website per line')}
            hint='For example https://www.yourleague.com. The Website Plugin only loads on these websites.'
            rows={4}
          />
          <SwitchField
            name='allowSandboxed'
            label={capitalizeWords('Allow Hidden Website Addresses')}
            hint="Some site builders, like GoDaddy, don't share the page's address. Turn this on if the Website Plugin is blocked there."
          />
        </div>
      </FormikProvider>
    </BaseModal>
  )
}

export default PluginKeyModal

const formStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const twoColumnStyle = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`
